/**
 * Instagram Store
 * Manages Instagram connection state, media, and stats
 * CRITICAL: Must work perfectly - zero tolerance for failures
 * 
 * @author Varun Pandey
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { InstagramConnection, InstagramMedia, InstagramStats } from '@/api/types';
import { storageManager } from '@/storage/storageManager';
import { STORAGE_KEYS } from '@/storage/storageKeys';

interface InstagramState {
  connection: InstagramConnection | null;
  media: InstagramMedia[];
  stats: InstagramStats | null;
  isLoading: boolean;
  error: string | null;
  lastSyncAt: string | null;
  
  // OAuth state
  oauthState: string | null;
  oauthContext:
    | 'photos'
    | 'videos'
    | 'onboarding'
    | 'dashboard'
    | 'profile'
    | 'login'
    | 'signup'
    | 'connect'
    | null;
  
  // Actions
  setConnection: (connection: InstagramConnection | null) => Promise<void>;
  setMedia: (media: InstagramMedia[]) => Promise<void>;
  setStats: (stats: InstagramStats | null) => Promise<void>;
  setOAuthState: (state: string | null, context?: InstagramState['oauthContext']) => Promise<void>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  updateLastSync: () => void;
  clearInstagramData: () => Promise<void>;
}

export const useInstagramStore = create<InstagramState>()(
  persist(
    (set, get) => ({
      connection: null,
      media: [],
      stats: null,
      isLoading: false,
      error: null,
      lastSyncAt: null,
      oauthState: null,
      oauthContext: null,

      setConnection: async (connection) => {
        set({ connection });
        const userId = connection?.instagramId || 'default';
        await storageManager.set(
          STORAGE_KEYS.INSTAGRAM_STATE(userId),
          connection
        );
      },

      setMedia: async (media) => {
        set({ media });
        const userId = get().connection?.instagramId || 'default';
        await storageManager.set(
          STORAGE_KEYS.INSTAGRAM_MEDIA(userId),
          media
        );
      },

      setStats: async (stats) => {
        set({ stats });
        const userId = get().connection?.instagramId || 'default';
        await storageManager.set(
          STORAGE_KEYS.INSTAGRAM_STATS(userId),
          stats
        );
      },

      setOAuthState: async (state, context = null) => {
        set({ oauthState: state, oauthContext: context });
        await storageManager.set(STORAGE_KEYS.OAUTH_STATE, state);
        if (context) {
          await storageManager.set(STORAGE_KEYS.OAUTH_CONTEXT, context);
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      updateLastSync: () => {
        set({ lastSyncAt: new Date().toISOString() });
      },

      clearInstagramData: async () => {
        const userId = get().connection?.instagramId || 'default';
        await storageManager.remove(STORAGE_KEYS.INSTAGRAM_STATE(userId));
        await storageManager.remove(STORAGE_KEYS.INSTAGRAM_MEDIA(userId));
        await storageManager.remove(STORAGE_KEYS.INSTAGRAM_STATS(userId));
        await storageManager.remove(STORAGE_KEYS.OAUTH_STATE);
        await storageManager.remove(STORAGE_KEYS.OAUTH_CONTEXT);
        set({
          connection: null,
          media: [],
          stats: null,
          oauthState: null,
          oauthContext: null,
          error: null,
        });
      },
    }),
    {
      name: 'instagram-storage',
      partialize: (state) => ({
        connection: state.connection,
        media: state.media,
        stats: state.stats,
        lastSyncAt: state.lastSyncAt,
      }),
    }
  )
);
